// frontend/admin-portal/src/pages/Treasury.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Alert,
  Badge,
  Input,
  Grid,
  ViewToggle,
  formatMoney,
} from "@fiberhome/shared-ui";
import { usePersistedState } from "@fiberhome/shared-ui";
import { treasuryService } from "../services/treasuryService";
import { useSettings } from "../contexts/SettingsContext";
import TreasuryStats from "../components/treasury/TreasuryStats";
import ProfitBreakdown from "../components/treasury/ProfitBreakdown";
import TransactionModal from "../components/treasury/TransactionModal";
import ExpectedProfitBreakdown from "../components/treasury/ExpectedProfitBreakdown";

const PER_PAGE_OPTIONS = [10, 20, 30, 50, 100];

function txTone(type) {
  return type === "in" ? "success" : type === "out" ? "danger" : "info";
}

function txSign(type) {
  return type === "in" ? "+" : type === "out" ? "−" : "";
}

function txColor(type) {
  return type === "in"
    ? "var(--fh-success)"
    : type === "out"
      ? "var(--fh-danger)"
      : "var(--fh-info)";
}

export default function Treasury() {
  const navigate = useNavigate();
  const { get } = useSettings();
  const settingsCurrency = get(
    "finance.baseCurrency",
    get("finance.currencySymbol", "IQD"),
  );

  const [view, setView] = usePersistedState("fiberhome.treasury.view", "list");

  const [dashboard, setDashboard] = useState(null);
  const [profit, setProfit] = useState(null);
  const [profitLoading, setProfitLoading] = useState(true);
  const [profitError, setProfitError] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 30,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 30,
    type: "",
    accountId: "",
    categoryId: "",
    from: "",
    to: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [expectedProfit, setExpectedProfit] = useState(null);
  const [expectedProfitLoading, setExpectedProfitLoading] = useState(true);
  const [expectedProfitError, setExpectedProfitError] = useState("");

  const loadRefs = useCallback(async () => {
    setExpectedProfitLoading(true);
    setExpectedProfitError("");
    try {
      const [d, c] = await Promise.all([
        treasuryService.dashboard(),
        treasuryService.listCategories({ isActive: true }),
      ]);
      setDashboard(d);
      setCategories(c);
      setExpectedProfit(d.expectedProfit || null); // ← new
    } catch (err) {
      setExpectedProfitError(err.response?.data?.message || err.message);
    } finally {
      setExpectedProfitLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } =
        await treasuryService.listTransactions(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadProfit = useCallback(async () => {
    setProfitLoading(true);
    setProfitError("");
    try {
      const data = await treasuryService.profit();
      setProfit(data);
    } catch (err) {
      setProfitError(err.response?.data?.message || err.message);
    } finally {
      setProfitLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRefs();
  }, [loadRefs, refreshKey]);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  useEffect(() => {
    loadProfit();
  }, [loadProfit, refreshKey]);

  const handleDelete = async (tx) => {
    if (!window.confirm(`Delete ${tx.code}? This will reverse the balance.`))
      return;
    try {
      await treasuryService.deleteTransaction(tx.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const baseCurrency = dashboard?.baseCurrency || settingsCurrency || "IQD";

  // ── Shared render helpers (used by both Table + Grid) ──────────────────
  const renderType = (r) => <Badge variant={txTone(r.type)}>{r.type}</Badge>;

  const renderAmount = (r) => (
    <span style={{ fontWeight: 600, color: txColor(r.type) }}>
      {txSign(r.type)}
      {formatMoney(r.amount, { symbol: r.currency })}
    </span>
  );

  // ── Column definitions for the Table view ─────────────────────────────
  const tableColumns = [
    {
      key: "occurredAt",
      label: "Date",
      render: (r) => new Date(r.occurredAt).toLocaleDateString(),
    },
    {
      key: "code",
      label: "Code",
      render: (r) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.code}</span>
      ),
    },
    { key: "type", label: "Type", render: renderType },
    { key: "amount", label: "Amount", render: renderAmount },
    {
      key: "account",
      label: "Account",
      render: (r) =>
        r.account ? `${r.account.name} (${r.account.currency})` : "—",
    },
    {
      key: "category",
      label: "Category",
      render: (r) => r.category?.name || <span className="text-muted">—</span>,
    },
    {
      key: "description",
      label: "Description",
      render: (r) => (
        <span style={{ fontSize: 12.5 }}>{r.description || "—"}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 80,
      render: (r) =>
        (r.referenceType === "manual" || r.referenceType === "opening") && (
          <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}>
            Delete
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Treasury"
        subtitle="Cash flow, investments, and free cash — Richest Man in Babylon style"
        breadcrumbs={["Home", "Treasury"]}
        actions={
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            + New Transaction
          </Button>
        }
      />

      <TreasuryStats refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="row g-3 mb-3">
        <div className="col-lg-7">
          <Card title={`Accounts (Base: ${baseCurrency})`}>
            <div className="row g-3">
              {(dashboard?.accounts || []).map((a) => (
                <div className="col-md-4 col-sm-6" key={a.id}>
                  <div className="fh-treasury-account-card">
                    <div className="fh-treasury-account-card__head">
                      <span
                        className="fh-treasury-account-card__dot"
                        style={{ background: a.color }}
                      />
                      <strong>{a.name}</strong>
                    </div>
                    <div className="fh-treasury-account-card__amount">
                      {formatMoney(a.currentBalance, { symbol: a.currency })}
                    </div>
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      {a.type} · {a.currency}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="col-lg-5">
          <div className="d-flex flex-column gap-3">
            <ProfitBreakdown
              profit={profit}
              loading={profitLoading}
              error={profitError}
              period="this month"
            />
            <ExpectedProfitBreakdown
              data={expectedProfit}
              loading={expectedProfitLoading}
              error={expectedProfitError}
            />
          </div>
        </div>
      </div>

      <Card className="mb-3">
        <div className="fh-supplier-filters">
          <select
            className="fh-input"
            value={filters.type}
            onChange={(e) =>
              setFilters({ ...filters, type: e.target.value, page: 1 })
            }
          >
            <option value="">All types</option>
            <option value="in">Money In</option>
            <option value="out">Money Out</option>
            <option value="transfer">Transfer</option>
          </select>
          <select
            className="fh-input"
            value={filters.accountId}
            onChange={(e) =>
              setFilters({ ...filters, accountId: e.target.value, page: 1 })
            }
          >
            <option value="">All accounts</option>
            {(dashboard?.accounts || []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.currency})
              </option>
            ))}
          </select>
          <select
            className="fh-input"
            value={filters.categoryId}
            onChange={(e) =>
              setFilters({ ...filters, categoryId: e.target.value, page: 1 })
            }
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <Input
            type="date"
            value={filters.from}
            onChange={(e) =>
              setFilters({ ...filters, from: e.target.value, page: 1 })
            }
          />
          <Input
            type="date"
            value={filters.to}
            onChange={(e) =>
              setFilters({ ...filters, to: e.target.value, page: 1 })
            }
          />
        </div>
      </Card>

      <Card
        title={`Transactions (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar">
            <ViewToggle value={view} onChange={setView} size="sm" />
            <div className="fh-products-toolbar__per-page">
              <span className="text-muted" style={{ fontSize: 12 }}>
                Per page
              </span>
              <select
                className="fh-input fh-input--compact"
                value={filters.limit}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    limit: Number(e.target.value),
                    page: 1,
                  })
                }
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="fh-products-toolbar__pager">
              <Button
                size="sm"
                variant="ghost"
                disabled={pagination.page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              >
                Prev
              </Button>
              <span className="text-muted" style={{ fontSize: 12 }}>
                Page {pagination.page} / {pagination.pages || 1}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        {view === "list" ? (
          <Table
            columns={tableColumns}
            data={items}
            loading={loading}
            emptyMessage="No transactions yet"
          />
        ) : (
          <Grid
            data={items}
            loading={loading}
            emptyMessage="No transactions yet"
            titleColumn="code"
            getId={(r) => r.id}
            onCardClick={(r) => navigate(`/treasury/${r.id}`)}
            headerExtra={(r) => renderType(r)}
            imageColumn={null}
            columns={[
              {
                key: "amount",
                label: "Amount",
                span: 12,
                render: renderAmount,
              },
              {
                key: "occurredAt",
                label: "Date",
                span: 6,
                render: (r) => new Date(r.occurredAt).toLocaleDateString(),
              },
              {
                key: "account",
                label: "Account",
                span: 6,
                render: (r) =>
                  r.account ? (
                    <span style={{ fontSize: 12 }}>
                      {r.account.name}
                      <span className="text-muted">
                        {" "}
                        · {r.account.currency}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  ),
              },
              {
                key: "category",
                label: "Category",
                span: 6,
                render: (r) =>
                  r.category?.name || <span className="text-muted">—</span>,
              },
              {
                key: "description",
                label: "Description",
                span: 12,
                render: (r) => (
                  <span style={{ fontSize: 12.5 }}>{r.description || "—"}</span>
                ),
              },
            ]}
            footerExtra={(r) =>
              (r.referenceType === "manual" ||
                r.referenceType === "opening") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation(); // don't trigger onCardClick
                    handleDelete(r);
                  }}
                >
                  Delete
                </Button>
              )
            }
          />
        )}
      </Card>

      <TransactionModal
        open={modalOpen}
        accounts={dashboard?.accounts || []}
        categories={categories}
        onClose={() => setModalOpen(false)}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
}
